/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Attribute, Directive, Host, Input, TemplateRef, ViewContainerRef} from '@angular/core';

import {NgLocalization, getPluralCategory} from '../localization';

import {SwitchView} from './ng_switch';
/**
 * \@ngModule CommonModule
 * 
 * \@whatItDoes Adds / removes DOM sub-trees based on a numeric value. Tailored for pluralization.
 * 
 * \@howToUse 
 * ```
 * <some-element [ngPlural]="value">
 *   <ng-template ngPluralCase="=0">there is nothing</ng-template>
 *   <ng-template ngPluralCase="=1">there is one</ng-template>
 *   <ng-template ngPluralCase="few">there are a few</ng-template>
 * </some-element>
 * ```
 * 
 * \@description 
 * 
 * Displays DOM sub-trees that match the switch expression value, or failing that, DOM sub-trees
 * that match the switch expression's pluralization category.
 * 
 * To use this directive you must provide a container element that sets the `[ngPlural]` attribute
 * to a switch expression. Inner elements with a `[ngPluralCase]` will display based on their
 * expression:
 * - if `[ngPluralCase]` is set to a value starting with `=`, it will only display if the value
 *   matches the switch expression exactly,
 * - otherwise, the view will be treated as a "category match", and will only display if exact
 *   value matches aren't found and the value maps to its category for the defined locale.
 * 
 * See http://cldr.unicode.org/index/cldr-spec/plural-rules
 * 
 * \@experimental
 */
export class NgPlural {
private _switchValue: number;
private _activeView: SwitchView;
private _caseViews: {[k: string]: SwitchView} = {};
/**
 * @param {?} _localization
 */
constructor(private _localization: NgLocalization) {}
/**
 * @param {?} value
 * @return {?}
 */
set ngPlural(value: number) {
    this._switchValue = value;
    this._updateView();
  }
/**
 * @param {?} value
 * @param {?} switchView
 * @return {?}
 */
addCase(value: string, switchView: SwitchView): void { this._caseViews[value] = switchView; }
/**
 * @return {?}
 */
private _updateView(): void {
    this._clearViews();

    const /** @type {?} */ cases = Object.keys(this._caseViews);
    const /** @type {?} */ key = getPluralCategory(this._switchValue, cases, this._localization);
    this._activateView(this._caseViews[key]);
  }
/**
 * @return {?}
 */
private _clearViews() {
    if (this._activeView) this._activeView.destroy();
  }
/**
 * @param {?} view
 * @return {?}
 */
private _activateView(view: SwitchView) {
    if (view) {
      this._activeView = view;
      this._activeView.create();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: '[ngPlural]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: NgLocalization, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'ngPlural': [{ type: Input },],
};
}

function NgPlural_tsickle_Closure_declarations() {
/** @type {?} */
NgPlural.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgPlural.ctorParameters;
/** @type {?} */
NgPlural.propDecorators;
/** @type {?} */
NgPlural.prototype._switchValue;
/** @type {?} */
NgPlural.prototype._activeView;
/** @type {?} */
NgPlural.prototype._caseViews;
/** @type {?} */
NgPlural.prototype._localization;
}

/**
 * \@ngModule CommonModule
 * 
 * \@whatItDoes Creates a view that will be added/removed from the parent {\@link NgPlural} when the
 *             given expression matches the plural expression according to CLDR rules.
 * 
 * \@howToUse 
 * ```
 * <some-element [ngPlural]="value">
 *   <ng-template ngPluralCase="=0">...</ng-template>
 *   <ng-template ngPluralCase="other">...</ng-template>
 * </some-element>
 * ```
 * 
 * See {\@link NgPlural} for more details and example.
 * 
 * \@experimental
 */
export class NgPluralCase {
/**
 * @param {?} value
 * @param {?} template
 * @param {?} viewContainer
 * @param {?} ngPlural
 */
constructor(
public value: string, template: TemplateRef<Object>,
      viewContainer: ViewContainerRef,  ngPlural: NgPlural) {
    const isANumber: boolean = !isNaN(Number(value));
    ngPlural.addCase(isANumber ? `=${value}` : value, new SwitchView(viewContainer, template));
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: '[ngPluralCase]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Attribute, args: ['ngPluralCase', ] }, ]},
{type: TemplateRef, },
{type: ViewContainerRef, },
{type: NgPlural, decorators: [{ type: Host }, ]},
];
}

function NgPluralCase_tsickle_Closure_declarations() {
/** @type {?} */
NgPluralCase.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgPluralCase.ctorParameters;
/** @type {?} */
NgPluralCase.prototype.value;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
